import {
  Account_Update,
  CompanyShare_Create,
  CompanyShareableType,
  Contact_Update,
  deleteV1CompaniesCompanyIdCompanySharesId,
  getV1CompaniesCompanyIdCompanyShares,
  ModelError_Response,
  patchV1CompaniesCompanyIdAccountsId,
  postV1CompaniesCompanyIdCompanyShares,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { isNil } from 'lodash-es';

import { AutocompleteBooleanOptionValue } from '~components/FormFields/CommonAutocompletes/AutocompleteBoolean';
import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { formatPhoneForSubmit } from '~utils/utilFunctions';

import { ConnectedAccountContactPanelHandler } from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import { TruckCapacityPanelHandler } from '../TruckCapacityPanel/TruckCapacityPanel';
import { AccountConfigurationPanelHandler } from './AccountConfigurationPanel/AccountConfigurationPanel';
import { AccountContactPanelHandler } from './AccountContactPanel/AccountContactPanel';
import { AccountDetailsPanelHandler } from './AccountDetailsPanel/AccountDetailsPanel';

export async function collectDetailsToUpdateAccount(
  ref: React.RefObject<AccountDetailsPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const address = AddressItem.deparseCreate(data.address as AddressItem);

  const values: Pick<
    Account_Update,
    | 'name'
    | 'department_id'
    | 'address'
    | 'billing_address'
    | 'external_id'
    | 'accounting_id'
    | 'label_ids'
  > = {
    name: data.name,
    department_id: data.department?.value || null,
    address,
    billing_address: data.isBillingAddressSame
      ? address
      : AddressItem.deparseCreate(data.billingAddress as AddressItem),
    external_id: data.externalId || null,
    accounting_id: data.accountingId || null,
    label_ids: data.labels?.map((label) => label.id),
  };

  return { values, companyId: data.company.value };
}

export async function collectContactsToUpdateConnectedAccount(
  ref: React.RefObject<ConnectedAccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const primaryContact: Contact_Update = {
    name: `${data.primaryContact.firstName} ${data.primaryContact.lastName}`,
    email: data.primaryContact.email || undefined,
    phone: formatPhoneForSubmit(data.primaryContact.phone),
  };
  const billingContact: Contact_Update = {
    name:
      data.billingContact?.firstName && data.billingContact.lastName
        ? `${data.billingContact.firstName} ${data.billingContact.lastName}`
        : undefined,
    email: data.billingContact?.email || undefined,
    phone: formatPhoneForSubmit(data.billingContact?.phone),
  };

  const values: Pick<Account_Update, 'primary_contact' | 'billing_contact'> = {
    primary_contact: primaryContact,
    billing_contact: data.isBillingContactSame ? primaryContact : billingContact,
  };

  return values;
}

export async function collectContactsToUpdateNonConnectedAccount(
  ref: React.RefObject<AccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const primaryContact: Contact_Update = {
    name: data.primaryContact?.name || undefined,
    email: data.primaryContact?.email || undefined,
    phone: formatPhoneForSubmit(data.primaryContact?.phone),
  };
  const billingContact: Contact_Update = {
    name: data.billingContact?.name || undefined,
    email: data.billingContact?.email || undefined,
    phone: formatPhoneForSubmit(data.billingContact?.phone),
  };

  const values: Pick<Account_Update, 'primary_contact' | 'billing_contact'> = {
    primary_contact: primaryContact,
    billing_contact: data.isBillingContactSame ? primaryContact : billingContact,
  };

  return values;
}

export async function collectConfigurationToUpdateConnectedAccount(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Update, 'auto_accept_orders' | 'show_user_phone_number'> = {
    auto_accept_orders:
      data.autoAcceptOrders.value === AutocompleteBooleanOptionValue.TRUE,
    show_user_phone_number: data.showUserPhoneNumber ?? true,
  };

  return values;
}

export async function collectNotesToUpdateAccount(
  ref: React.RefObject<NotesPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Update, 'notes'> = {
    notes: data.notes?.trim() || null,
  };

  return values;
}

export async function collectScalarTruckCountToUpdateAccount(
  ref: React.RefObject<TruckCapacityPanelHandler>,
  isConnectedAccount: boolean,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const truckCount =
    data.truckCount === '' || isNil(data.truckCount)
      ? undefined
      : Number(data.truckCount);

  const values: Pick<Account_Update, 'truck_count' | 'connected_account'> =
    isConnectedAccount
      ? { connected_account: { truck_count: truckCount } }
      : { truck_count: truckCount };

  return values;
}

export async function updateAccount(
  data: Account_Update,
  accountId: string,
  companyId: string,
) {
  try {
    const response = await patchV1CompaniesCompanyIdAccountsId({
      path: { 'company-id': companyId, id: accountId },
      body: data,
    });

    return { account: Account.parse(response.data.data) };
  } catch (error) {
    const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

    if (isUnprocessableEntityError) {
      const typedError = error as AxiosError<ModelError_Response>;

      return { errors: typedError.response?.data.error.errors ?? [] };
    }

    throw error;
  }
}

type CompanySharesDiff = { toCreate: CompanyShare_Create[]; toDelete: string[] };

async function getCompanySharesDiff(
  companyShareableType: CompanyShareableType,
  updatedSelection: Array<{ label: string; value: string }>,
  companyId: string,
  receiverCompanyId: string,
) {
  const currentCompanySharesResponse = await getV1CompaniesCompanyIdCompanyShares({
    path: { 'company-id': companyId },
    query: {
      'filter[company_shareable_type]': companyShareableType,
      'filter[between_company_id]': receiverCompanyId,
    },
  });
  const currentCompanyShares = currentCompanySharesResponse.data.data
    .map((item) => CompanyShare.parse(item))
    .filter((item) => item.senderCompany.id === companyId);

  const diff: CompanySharesDiff = { toCreate: [], toDelete: [] };

  updatedSelection.forEach((option) => {
    const isAlreadyShared = currentCompanyShares.some((companyShare) => {
      return companyShare.companyShareable.id === option.value;
    });

    if (!isAlreadyShared) {
      diff.toCreate.push({
        receiver_company_id: receiverCompanyId,
        company_shareable_id: option.value,
        company_shareable_type: companyShareableType as CompanyShareableType,
      });
    }
  });

  currentCompanyShares.forEach((companyShare) => {
    const isStillShared = updatedSelection.some((option) => {
      return option.value === companyShare.companyShareable.id;
    });

    if (!isStillShared) {
      diff.toDelete.push(companyShare.id);
    }
  });

  return diff;
}

async function processCompanySharesDiff(diff: CompanySharesDiff, companyId: string) {
  const createCompanySharePromises = diff.toCreate.map((item) => {
    return postV1CompaniesCompanyIdCompanyShares({
      path: { 'company-id': companyId },
      body: item,
    });
  });
  const deleteCompanySharePromises = diff.toDelete.map((item) => {
    return deleteV1CompaniesCompanyIdCompanySharesId({
      path: { 'company-id': companyId, id: item },
    });
  });
  const createCompanyShareResponses = await Promise.all(createCompanySharePromises);
  await Promise.all(deleteCompanySharePromises);

  return {
    created: createCompanyShareResponses.map((response) => {
      return CompanyShare.parse(response.data.data);
    }),
    deleted: diff.toDelete,
  };
}

export async function diffCompanyShares(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
  updatedAccount: Account,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const companyId = updatedAccount.company?.id;
  const receiverCompanyId = updatedAccount.connectedCompany?.id;

  if (!receiverCompanyId || !companyId) {
    return undefined;
  }

  const sharedDriversDiff = await getCompanySharesDiff(
    CompanyShareableType.USER,
    data.drivers ?? [],
    companyId,
    receiverCompanyId,
  );
  const sharedEquipmentDiff = await getCompanySharesDiff(
    CompanyShareableType.EQUIPMENT,
    data.equipment ?? [],
    companyId,
    receiverCompanyId,
  );

  return { drivers: sharedDriversDiff, equipment: sharedEquipmentDiff };
}

export async function updateCompanyShares(
  driversDiff: CompanySharesDiff,
  equipmentDiff: CompanySharesDiff,
  updatedAccount: Account,
) {
  const companyId = updatedAccount.company?.id;
  const receiverCompanyId = updatedAccount.connectedCompany?.id;

  if (!receiverCompanyId || !companyId) {
    return undefined;
  }

  const drivers = await processCompanySharesDiff(driversDiff, companyId);
  const equipment = await processCompanySharesDiff(equipmentDiff, companyId);

  return { drivers, equipment };
}
